import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
  CmdSwitch,
} from '@commander-services/gui-components';
import { useRecoilValue } from 'recoil';
import { Controller, useForm } from 'react-hook-form';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../../Services/FormikValidatorService';
import PermissionsService, { PERMISSION_PARKING_TAG } from '../../../Services/PermissionsService';
import usePageWithForm from '../../../hooks/usePageWithForm';
import { postRequest, putRequest } from '../../../Services/HttpService';
import { customersAtom, ICustomer } from '../../../store/recoil/customers';
import { getCustomerNameById } from '../../Customers/CustomerService';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import useAnalytics from '../../../hooks/useAnalytics';

interface ModalFormProps {
  handleClose: () => void;
  data: any;
  id: number | null;
  customerId: number | null;
  updateTable: () => void;
}

function ParkingTagForm(props: ModalFormProps): JSX.Element {
  usePageWithForm();
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const allCustomerData = useRecoilValue<ICustomer[]>(customersAtom);
  const { formatMessage: f } = useIntl();
  const { data, id } = props;
  const customerId = data.filter((i) => i.indexId === 'customerId')[0];
  const { trackEvent } = useAnalytics();

  const filterCustomerIdByPermissions =
    customerId.values &&
    customerId.values.filter((item) =>
      PermissionsService.hasAccess(PERMISSION_PARKING_TAG, ['create'], item.value)
    );
  const name = data.filter((i) => i.indexId === 'name')[0];
  if (name) {
    name.rules = name.rules.map((i) => {
      return i.param === 'min'
        ? { param: 'minLength', value: 3, message: f({ id: 'carsGroups.titleError' }) }
        : i;
    });
  }
  const color = data.filter((i) => i.indexId === 'color')[0];
  const active = data.filter((i) => i.indexId === 'active')[0];

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      customerId: customerId?.value,
      name: name?.value,
      color: color && color.value ? color.value : '#000000',
      active: (active && active.value === true) || active?.value === null ? 1 : 0,
    },
    resolver: async (formData) => {
      const formValidation: IValidationApiResponse | null = {
        customerId: customerId.rules,
        name: name.rules,
        color: color.rules,
        active: active.rules,
      };

      const validateErros = validateFields(formData, formValidation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : formData, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  React.useEffect(() => {
    if (!id && filterCustomerIdByPermissions && filterCustomerIdByPermissions.length === 1) {
      setValue('customerId', filterCustomerIdByPermissions[0]?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCustomerIdByPermissions]);

  // create
  const create = async (values) => {
    trackEvent(['parking tag', 'click', 'Create parking tag']);
    setIsLoaderActive(true);
    const response = await postRequest('/v1/parking-tag/add', values);
    setIsLoaderActive(false);
    if (response) {
      props.updateTable();
      props.handleClose();
    }
  };

  // update
  const update = async (values) => {
    trackEvent(['parking tag', 'click', 'Update parking tag']);
    setIsLoaderActive(true);
    const response = await putRequest(
      `/v1/parking-tag/${id}/edit${`?customerId=${props.customerId}`}`,
      values
    );
    setIsLoaderActive(false);
    if (response) {
      props.updateTable();
      props.handleClose();
    }
  };

  // modal adding title and buttons
  let buttons: CmdModalFooterButton[] = [
    {
      id: 'modalClose',
      type: 'button',
      title: f({ id: 'form.close' }),
      className: 'e-button',
      closeCallback: props.handleClose,
    },
    {
      id: 'submitAddForm',
      type: 'submit',
      title: f({ id: 'form.save' }),
      className: 'e-button e-button--gray',
      submitCallback: handleSubmit(id ? update : create),
    },
  ];
  let title = 'table.add';
  let isAllowed = true;

  // modal editing title and buttons
  if (id) {
    buttons = [
      {
        id: 'modalClose',
        type: 'button',
        title: f({ id: 'form.close' }),
        className: 'e-button',
        closeCallback: props.handleClose,
      },
    ];
    isAllowed = PermissionsService.hasAccess(PERMISSION_PARKING_TAG, ['update'], props.customerId);
    if (isAllowed) {
      buttons.push({
        id: 'submitEditForm',
        type: 'submit',
        title: f({ id: 'form.save' }),
        className: 'e-button e-button--gray',
        submitCallback: handleSubmit(id ? update : create),
      });
    }
    title = 'table.detail';
  }

  // form

  return (
    <CmdModal
      id="parkingtag-formik-form"
      title={f({ id: `${title}` })}
      footerButtons={buttons}
      loader={isLoaderActive}
    >
      {isAllowed ? (
        <div>
          {id || customerId.value || filterCustomerIdByPermissions.length === 1 ? (
            <CmdLabel
              title={f({ id: 'systemSettings.customer' })}
              value={
                !id && filterCustomerIdByPermissions && filterCustomerIdByPermissions.length
                  ? filterCustomerIdByPermissions[0]?.item
                  : getCustomerNameById(allCustomerData, customerId.value)
              }
            />
          ) : (
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  label={f({ id: 'systemSettings.customer' })}
                  id="customerId"
                  introOption={f({ id: 'form.choose' })}
                  onChangeCallback={(value) => {
                    setValue('customerId', value);
                    trigger('customerId');
                    trackEvent(['parking tag', 'change', 'Select customer']);
                  }}
                  introDisabled={false}
                  options={filterCustomerIdByPermissions}
                  disabled={customerId.disabled}
                  required={FormikValidatorService.isFieldRequired(customerId.rules)}
                  error={errors.customerId?.message as string}
                  touched={true}
                />
              )}
            />
          )}
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                label={f({ id: 'tripsOverview.modal.parkingUsage' })}
                id="name"
                type="text"
                onChangeCallback={(value) => {
                  setValue('name', value);
                  trigger('name');
                }}
                required={FormikValidatorService.isFieldRequired(
                  name && name.rules ? name.rules : []
                )}
                error={errors.name?.message as string}
                touched={true}
              />
            )}
          />
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <CmdInput
                {...field}
                label={f({ id: 'systemSettings.color' })}
                id="color"
                type="color"
                onChangeCallback={(value) => {
                  setValue('color', value);
                  trigger('color');
                }}
                required={FormikValidatorService.isFieldRequired(color?.rules ? color.rules : [])}
                error={errors.color?.message as string}
                touched={true}
              />
            )}
          />
          <Controller
            name="active"
            control={control}
            render={({ field }) => (
              <CmdSwitch
                {...field}
                label={f({ id: 'operatingCostTypeForm.active' })}
                id="active"
                setValue={(value) => {
                  setValue('active', value);
                  trigger('active');
                  trackEvent(['parking tag', 'change', 'Switch active']);
                }}
                data-cy={
                  String(watch('active')) === '1' ? 'switch-is-active' : 'switch-is-inactive'
                }
              />
            )}
          />
        </div>
      ) : (
        <div className="form-horizontal">
          <CmdLabel
            title={f({ id: 'systemSettings.customer' })}
            value={getCustomerNameById(allCustomerData, customerId.value)}
            areLabels={true}
          />
          <CmdLabel
            title={f({ id: 'tripsOverview.modal.parkingUsage' })}
            value={watch('name')}
            areLabels={true}
          />
          <CmdLabel
            title={f({ id: 'systemSettings.color' })}
            value={watch('color')}
            areLabels={true}
          />
          <CmdLabel
            title={f({ id: 'operatingCostTypeForm.active' })}
            value={
              watch('active') ? f({ id: 'systemSettings.yes' }) : f({ id: 'systemSettings.no' })
            }
            areLabels={true}
          />
        </div>
      )}
    </CmdModal>
  );
}

export default ParkingTagForm;
